
import React from 'react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useLanguage } from '@/context/LanguageContext';
import { Check, X, Star, Globe, Shield, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '@/components/shared/SEO';
import { PatientROICalculator } from '@/components/conversion/PatientROICalculator';
import { WalletTeaser } from '@/components/conversion/WalletTeaser';
import { TrustBadgesSection } from '@/components/conversion/TrustBadgesSection';

const Pricing = () => {
  const { t, getLocalizedPath, language } = useLanguage();
  
  const clientPlans = [
    {
      name: t('basicPlan'),
      price: '0',
      period: t('forever'),
      description: t('basicPlanDescription'),
      features: [
        { name: t('browseTherapists'), included: true },
        { name: t('readReviews'), included: true },
        { name: t('basicSearch'), included: true },
        { name: t('oneTimeBooking'), included: true },
        { name: t('emailSupport'), included: true },
        { name: t('advancedFilters'), included: false },
        { name: t('prioritySupport'), included: false },
        { name: t('sessionRecordings'), included: false }
      ],
      recommended: false,
      cta: t('getStarted')
    },
    {
      name: t('premiumPlan'),
      price: '29',
      period: t('perMonth'),
      description: t('premiumPlanDescription'),
      features: [
        { name: t('allBasicFeatures'), included: true },
        { name: t('advancedFilters'), included: true },
        { name: t('priorityBooking'), included: true },
        { name: t('sessionReminders'), included: true },
        { name: t('progressTracking'), included: true },
        { name: t('prioritySupport'), included: true },
        { name: t('sessionRecordings'), included: false },
        { name: t('familyAccounts'), included: false }
      ],
      recommended: true,
      cta: t('startFreeTrial')
    },
    {
      name: t('familyPlan'),
      price: '79',
      period: t('perMonth'),
      description: t('familyPlanDescription'),
      features: [
        { name: t('allPremiumFeatures'), included: true },
        { name: t('familyAccounts'), included: true },
        { name: t('sessionRecordings'), included: true },
        { name: t('familyTherapy'), included: true },
        { name: t('dedicatedSupport'), included: true },
        { name: t('customReports'), included: true },
        { name: t('flexibleScheduling'), included: true },
        { name: t('emergencySupport'), included: true }
      ],
      recommended: false,
      cta: t('contactSales')
    }
  ];


  const sessionPricing = [
    {
      region: t('northAmerica'),
      priceRange: '$80 - $200',
      averagePrice: '$120',
      currency: 'USD',
      flag: '🇺🇸'
    },
    {
      region: t('westernEurope'),
      priceRange: '€60 - €150',
      averagePrice: '€90',
      currency: 'EUR',
      flag: '🇪🇺'
    },
    {
      region: t('easternEurope'),
      priceRange: '€30 - €80',
      averagePrice: '€50',
      currency: 'EUR',
      flag: '🇵🇱'
    },
    {
      region: t('latinAmerica'),
      priceRange: '$25 - $70',
      averagePrice: '$40',
      currency: 'USD',
      flag: '🇲🇽'
    },
    {
      region: t('asia'),
      priceRange: '$20 - $60',
      averagePrice: '$35',
      currency: 'USD',
      flag: '🇮🇳'
    },
    {
      region: t('oceania'),
      priceRange: 'A$90 - A$180',
      averagePrice: 'A$130',
      currency: 'AUD',
      flag: '🇦🇺'
    }
  ];

  return (
    <>
      <SEO
        title={t('pricing')}
        description={t('pricingMetaDescription')}
        keywords="therapy pricing, psychotherapy costs, online therapy rates, mental health services pricing"
        language={language}
        canonicalUrl={`https://psychotherapist.help${getLocalizedPath('/pricing')}`}
      />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-blue-50 to-white py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Transparent Therapy Pricing Worldwide
            </h1>
            <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
              Pay per session. No subscriptions. No hidden fees. Find affordable mental health care that fits your budget.
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              <Badge variant="secondary">Pay Per Session</Badge>
              <Badge variant="secondary">No Monthly Fees</Badge>
              <Badge variant="secondary">Save up to 70%</Badge>
            </div>
          </div>
        </div>
      </div>

      {/* Session Pricing by Region - MAIN FOCUS */}
      <div className="py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <Badge className="mb-4">Regional Pricing</Badge>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Therapy Sessions Worldwide
              </h2>
              <p className="text-lg text-muted-foreground mb-6">
                Find affordable quality therapy in your region or save up to 70% by choosing therapists in affordable countries
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {sessionPricing.map((region, index) => (
                <Card key={index} className="hover:shadow-lg transition-all border-2 hover:border-primary">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <span className="text-3xl">{region.flag}</span>
                      <h3 className="text-xl font-semibold">
                        {region.region}
                      </h3>
                    </div>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Price Range:</span>
                        <span className="font-semibold text-lg">{region.priceRange}</span>
                      </div>
                      <div className="flex justify-between items-center pt-2 border-t">
                        <span className="text-muted-foreground">Average:</span>
                        <span className="font-bold text-xl text-primary">{region.averagePrice}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            
            {/* Savings Calculator */}
            <Card className="border-2 border-primary/50 bg-gradient-to-br from-primary/5 to-accent/5">
              <CardContent className="p-8">
                <div className="text-center">
                  <h3 className="text-2xl font-bold mb-4">Compare Your Savings</h3>
                  <p className="text-muted-foreground mb-6">
                    Example: 8 sessions per month
                  </p>
                  
                  <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
                    <div className="bg-card rounded-lg p-6 border">
                      <p className="text-sm text-muted-foreground mb-2">North America</p>
                      <p className="text-3xl font-bold mb-1">$960</p>
                      <p className="text-xs text-muted-foreground">per month</p>
                    </div>
                    <div className="bg-card rounded-lg p-6 border">
                      <p className="text-sm text-muted-foreground mb-2">Eastern Europe</p>
                      <p className="text-3xl font-bold text-green-600 mb-1">$400</p>
                      <p className="text-xs text-muted-foreground">per month</p>
                    </div>
                    <div className="bg-primary text-primary-foreground rounded-lg p-6">
                      <p className="text-sm mb-2">Your Savings</p>
                      <p className="text-3xl font-bold mb-1">$560</p>
                      <p className="text-xs">58% saved</p>
                    </div>
                  </div>
                  
                  <p className="text-sm text-muted-foreground mt-6 mb-4">
                    Same quality care, certified professionals, your language - just more affordable
                  </p>
                  
                  <Link to={getLocalizedPath('/find')}>
                    <Button size="lg" className="px-8">
                      Explore All Therapists
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* For Clients - Simple Pricing */}
      <div className="py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="border-2 border-primary">
              <CardContent className="p-8">
                <div className="text-center mb-8">
                  <Badge className="mb-4">For Clients</Badge>
                  <h2 className="text-3xl font-bold mb-4">Simple Pay-Per-Session</h2>
                  <p className="text-lg text-muted-foreground mb-6">
                    No subscriptions. No monthly fees. Just pay for the sessions you book.
                  </p>
                </div>
                
                <div className="grid md:grid-cols-3 gap-6 mb-8">
                  <div className="text-center p-4 bg-accent/20 rounded-lg">
                    <div className="text-3xl font-bold text-primary mb-2">$0</div>
                    <p className="text-sm text-muted-foreground">Monthly Fee</p>
                  </div>
                  <div className="text-center p-4 bg-accent/20 rounded-lg">
                    <div className="text-3xl font-bold text-primary mb-2">$25-$200</div>
                    <p className="text-sm text-muted-foreground">Per Session</p>
                  </div>
                  <div className="text-center p-4 bg-accent/20 rounded-lg">
                    <div className="text-3xl font-bold text-primary mb-2">Free</div>
                    <p className="text-sm text-muted-foreground">First Consultation</p>
                  </div>
                </div>
                
                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-green-600" />
                    <span>Browse all therapists for free</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-green-600" />
                    <span>Pay only for sessions you attend</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-green-600" />
                    <span>Cancel anytime with no penalty</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-green-600" />
                    <span>First consultation often free or discounted</span>
                  </div>
                </div>
                
                <Link to={getLocalizedPath('/find')}>
                  <Button size="lg" className="w-full">
                    Find Your Therapist
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>


      {/* Value Proposition */}
      <div className="bg-accent/10 py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              Why Choose Our Platform
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8 mb-12">
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <Globe className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Global Access</h3>
                <p className="text-muted-foreground">
                  Access 5,000+ therapists worldwide in 40+ languages
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Quality Assured</h3>
                <p className="text-muted-foreground">
                  All therapists certified by Dr. José Luis Marín's Master program
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">24/7 Availability</h3>
                <p className="text-muted-foreground">
                  Book online sessions anytime with immediate availability
                </p>
              </div>
            </div>
            
            <div className="text-center space-y-6">
              <Link to={getLocalizedPath('/find')}>
                <Button size="lg" className="px-8">
                  Find Your Therapist
                </Button>
              </Link>
              
              <p className="text-sm text-muted-foreground">
                Are you a mental health professional?{' '}
                <Link to={getLocalizedPath('/for-therapists')} className="text-primary hover:underline font-medium">
                  Learn about joining our platform
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* ROI Calculator - Sprint 3 */}
      <PatientROICalculator />
      
      {/* Wallet Teaser - Sprint 2 */}
      <WalletTeaser />
      
      {/* Trust Badges - Sprint 2 */}
      <TrustBadgesSection />
    </>
  );
};

export default Pricing;
